import React, { useState, useCallback, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useAuth0 } from '@auth0/auth0-react';
import { useProjects } from '../contexts/ProjectsContext';
import { useIsPremium } from '../contexts/PremiumContext';
import { CALENDLY_URL, openCalendly } from '../utils/calendly';
import PremiumLock, { LockedCard } from './PremiumLock/PremiumLock';
import '../styles/Projects.css';
import './PremiumLock/PremiumLock.css';

// Every premium-gated CTA routes to Calendly with email prefill
// (Implementation Brief 2026-06-07). UPGRADE_URL kept for any
// remaining anchor-tag fallback; click handlers should call
// openCalendly(user) so the email pre-fills.
const UPGRADE_URL = CALENDLY_URL;
const FREE_TIER_PROJECT_CAP = 3;
import {
  OVERLAP_CATEGORIES, EEA_ONLY_CATEGORIES, OSM_ONLY_CATEGORIES,
  readEea, readOsm, mergePair, fmtMeters, pairBucket, singleBucket,
} from '../utils/industrialMerge';
import { nullDisplay } from '../utils/fieldProvenance';
import { ProvenanceIcon } from './ProvenancePopover';
import { ScoreCell } from './ScoreBreakdown';
import { GridQueueCell } from './GridQueuePopover';
import { TimelineView } from './Timeline';
import { PrintReport } from './PrintReport';
import { tierForIndex, SeidrIndexExplainer } from './SeidrIndex';
import '../styles/PrintReport.css';

// ── AI Analysis helpers ──────────────────────────────────────────────
// Routes through our backend proxy at /api/v1/grid-feasibility/* —
// see backend/app/api/grid_feasibility_proxy.py for why.
async function fetchGridFeasibility(lat, lon, mw, kv) {
  const API_BASE = import.meta.env.VITE_API_URL || '';
  const voltageParam = kv >= 420 ? '420kV' : kv >= 300 ? '300kV' : kv >= 132 ? '132kV' : '66kV';
  // Upstream enum is 'datacenter' (no underscore). 'data_center' returns
  // HTTP 422 (audit 2026-05-29).
  const body = { lat, lng: lon, requested_mw: mw || 20, project_type: 'datacenter', connection_voltage: voltageParam, project_window_years: 5 };
  const queueQs = new URLSearchParams({ lat, lng: lon, radius_km: 15 }).toString();
  const [assessResp, queueResp, analyzeResp] = await Promise.all([
    fetch(`${API_BASE}/api/v1/grid-feasibility/assess`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    fetch(`${API_BASE}/api/v1/grid-feasibility/queue?${queueQs}`),
    fetch(`${API_BASE}/api/v1/grid-feasibility/analyze`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, include_advisors: ['queue_drop', 'timeline', 'reinforcement'] }) }),
  ]);
  const result = {};
  if (assessResp.ok) result.feasibility = await assessResp.json();
  if (queueResp.ok) result.queue = await queueResp.json();
  if (analyzeResp.ok) result.aiAnalysis = await analyzeResp.json();
  return result;
}

// ── Backend readiness adapter (audit 2026-05-28) ─────────────────────
//
// Local calcScore + SCORE_FACTORS (13-factor FI-tuned heuristic) removed
// 2026-05-28. Was returning divergent scores from the backend
// readiness_score on the same site (52 vs 75 Conditional vs Suitable)
// because most factors were FI-only and returned null on NO sites.
// Backend powered_land_readiness is the single source of truth now;
// when a project's enrichment doesn't yet carry readiness_score the UI
// shows "Score pending" rather than inventing a divergent number.
// When the saved enrichment carries `readiness_score` + `readiness_themes`
// (populated by the powered_land_readiness service for every DC site),
// use THAT as the score instead of recomputing locally. The local
// calcScore was designed against FI-only fields (dual_feed, broadband,
// fi_cultural_heritage, etc.); for NO sites half its factors return
// null and the resulting headline number disagreed wildly with the
// site-analysis-panel score (52 vs 75 on the same parcel). The backend
// readiness model is the authoritative one — Compare should mirror it.
// Same {pct, rating, components, factors} shape so ScoreCell renders.
const THEME_LABELS = {
  power: 'Power & Grid', planning: 'Planning & Zoning', water: 'Water Access',
  site_formation: 'Site Formation', access: 'Road Access', fiber: 'Network',
  environment: 'Environment', permitting: 'Permitting', expansion: 'Expansion',
};
function calcScoreFromReadiness(s) {
  const pct = Math.round(Number(s.readiness_score) || 0);
  // Tier label from shared SeidrIndex module (Strong / Suitable /
  // Conditional / Weak). Keep this in sync with tierForIndex().
  const { tier: rating } = tierForIndex(pct);
  const themes = s.readiness_themes || {};
  // One component = one theme. ScoreBreakdownPopover treats each component
  // independently (own bar) which works here because backend themes are
  // already the right grain.
  const components = Object.entries(themes).map(([key, t]) => {
    const s01 = Number(t?.score_0_1 ?? 0);
    const weight = Number(t?.weight ?? 0);
    const pts = Number(t?.weighted_pts ?? s01 * weight);
    return {
      name: THEME_LABELS[key] || key,
      factors: [],            // backend doesn't break themes into factors
      pts, max: weight,
      raw: t?.defaulted ? null : Math.round(s01 * 100),
      weightPct: weight / 100, // backend weights sum to ~100
      contribution: pts,
      defaulted: !!t?.defaulted,
    };
  });
  // Factors: synthesize one per theme for the drivers/penalties section.
  // Defaulted themes get scoreVal=null so they appear in "Not scored".
  const factors = Object.entries(themes).map(([key, t]) => ({
    key,
    label: THEME_LABELS[key] || key,
    weight: Number(t?.weight ?? 0),
    scoreVal: t?.defaulted ? null : Number(t?.score_0_1 ?? 0),
    valueText: t?.defaulted ? 'Not measured' : `${Math.round((t?.score_0_1 ?? 0) * 100)}/100`,
    contribution: Number(t?.weighted_pts ?? 0),
  }));
  return { pct, rating, components, factors, totalMax: 100, source: 'backend' };
}

// ── Best-in-row helpers (audit 2026-05-28) ──────────────────────────
// Rows tag themselves with dir: 'lower' | 'higher' | 'truthy'. The render
// code extracts a comparable number from each cell's raw value (preferring
// common numeric subkeys when raw is an object) and highlights the unique
// best cell so the reader can find what differs without scanning every cell.
// Rows without dir aren't comparable (info / names / categorical text).
function extractCompareNum(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  if (typeof raw === 'object') {
    // Numeric subkey priority — pick the one that represents the metric.
    for (const k of ['km', 'mw', 'distance_km', 'depth_m', 'pct', 'suit',
                     'total', 'headroom', 'realistic', 'm', 'count']) {
      if (typeof raw[k] === 'number') return raw[k];
    }
  }
  return null;
}
function pickBestIndex(nums, dir) {
  const valid = nums.map((n, i) => [n, i]).filter(([n]) => n != null);
  if (valid.length < 2) return null;
  const sorted = [...valid].sort((a, b) => dir === 'lower' ? a[0] - b[0] : b[0] - a[0]);
  if (sorted.length >= 2 && sorted[0][0] === sorted[1][0]) return null; // tie → no highlight
  return sorted[0][1];
}

// ── Comparison criteria ─────────────────────────────────────────────
const CRITERIA = [
  { g: 'Power', l: 'Substation', field: 'nearest_grid_name', s: 'info',
    v: s => s.nearest_grid_name || null, f: v => v || '—', c: () => 'gray' },
  // Operator (Nätägare) — Fingrid (TSO) preferred for >50 MW projects;
  // DSO (Elenia/Caruna/Helen) is a soft constraint for hyperscale.
  { g: 'Power', l: 'Operator', field: 'nearest_grid_operator',
    v: s => s.nearest_grid_operator || null,
    f: v => v || '—',
    c: v => !v ? 'gray' : /fingrid/i.test(v) ? 'green' : 'amber',
  },
  // Phase 2 thresholds: <2 km good (CAPEX <€2M), <5 km manageable, >5 km hard.
  { g: 'Power', l: 'Grid distance', field: 'nearest_grid_distance_km', dir: 'lower',
    v: s => s.nearest_grid_distance_km, f: v => v != null ? `${v.toFixed(1)} km` : '—',
    c: v => v == null ? 'gray' : v < 2 ? 'green' : v < 5 ? 'amber' : 'red' },
  // Phase 2: ≥220 kV is hyperscale-ready; 110 kV needs upgrade for >100 MW;
  // <110 kV is a hard cap below ~30 MW. Conservative coloring against an
  // implicit 100 MW target (Phase 4 will make this MW-aware).
  { g: 'Power', l: 'Voltage', field: 'nearest_grid_voltage_kv', dir: 'higher',
    v: s => s.nearest_grid_voltage_kv, f: v => v != null ? `${v} kV` : '—',
    c: v => v == null ? 'gray' : v >= 220 ? 'green' : v >= 110 ? 'amber' : 'red' },
  { g: 'Power', l: 'Dual feed', field: 'dual_feed_available', dir: 'truthy',
    v: s => s.dual_feed_available, f: v => v === true ? 'Yes' : v === false ? 'No' : '—',
    c: v => v === true ? 'green' : v === false ? 'red' : 'gray' },
  // Grid queue (15 km) — competing connection projects already in the TSO
  // queue. High queue MW = harder to secure capacity / longer lead time /
  // potential reinforcement charges. Auto-fetched eagerly per site so the
  // Compare table is fully populated without a per-row click. Cell is a
  // GridQueueCell button that opens a popover listing each individual
  // project (name, MW, drop probability, target connection date).
  { g: 'Power', l: 'Grid queue (15 km)', field: 'grid_queue_total_mw',
    v: s => ({
      data: s._grid_queue,
      // undefined = fetch hasn't run yet; null = fetch ran + failed
      // (HTTP error or upstream down); object = fetch returned data.
      state: s._grid_queue === undefined ? 'pending'
           : s._grid_queue === null ? 'failed'
           : (s._grid_queue.projects?.length || 0) === 0 && (s._grid_queue.total_queue_mw || 0) === 0
             ? 'clear'
             : 'has_queue',
    }),
    f: (v, s) => {
      if (v.state === 'pending') return 'Loading…';
      if (v.state === 'failed')  return 'Not fetched · run queue check';
      if (v.state === 'clear')   return 'Queue clear (no competing projects)';
      return <GridQueueCell queue={v.data} siteName={s?._project?.site_label} />;
    },
    c: v => {
      if (v.state === 'pending' || v.state === 'failed') return 'gray';
      if (v.state === 'clear') return 'green';
      const mw = v.data?.total_queue_mw || 0;
      return mw < 100 ? 'green' : mw < 500 ? 'amber' : 'red';
    },
  },
  // Phase 2: <50 km optimal, <150 km workable, >150 km informational only
  // (no DC project is killed by IXP distance alone — it's latency-tier).
  { g: 'Connectivity', l: 'IXP distance', field: 'nearest_ixp_distance_km', dir: 'lower',
    v: s => s.nearest_ixp_distance_km, f: v => v != null ? `${v.toFixed(0)} km` : '—',
    c: v => v == null ? 'gray' : v <= 50 ? 'green' : v <= 150 ? 'amber' : 'red' },
  // ── Unified Industrial Proximity (Sprint E · Task 3) — merges EEA
  // Industrial Reporting (large regulated facilities, authoritative) with
  // OSM proximity (smaller crowdsourced). For overlap categories we collapse
  // when both sources agree (within 2 km) and surface both with explicit
  // tags when they disagree, instead of showing two visually-identical rows.
  ...((() => {
    const fmtEeaOnly = e => {
      if (e == null) return '—';
      if (e.m == null) return 'None within 30 km';
      const company = e.company ? ` · ${e.company.slice(0, 18)}` : '';
      const lcp = e.isLcp ? ' · LCP' : '';
      return `${fmtMeters(e.m)}${company}${lcp} (EEA)${e.warn ? ' ⚠' : ''}`;
    };
    const fmtOsmOnly = o => {
      if (o == null) return '—';
      if (o.m == null) return 'None within 30 km';
      return `${fmtMeters(o.m)} (OSM)${o.warn ? ' ⚠' : ''}`;
    };
    const fmtPair = (m) => {
      if (m.kind === 'no_data') return '—';
      if (m.kind === 'none')    return 'None within 30 km';
      if (m.kind === 'agree')   return `${fmtMeters(m.m)} (EEA·OSM)`;
      if (m.kind === 'eea_only') return fmtEeaOnly(m.eea);
      if (m.kind === 'osm_only') return fmtOsmOnly(m.osm);
      // disagree — show both
      return `EEA ${fmtMeters(m.eea.m)}${m.eea.warn ? ' ⚠' : ''} · OSM ${fmtMeters(m.osm.m)}${m.osm.warn ? ' ⚠' : ''}`;
    };
    const eeaOnlyRows = EEA_ONLY_CATEGORIES.map(({ key, label, thresholdKey, field }) => ({
      g: 'Industrial Proximity', l: label, field,
      v: s => readEea(s, key),
      f: fmtEeaOnly,
      c: e => singleBucket(e, thresholdKey),
    }));
    const overlapRows = OVERLAP_CATEGORIES.map(({ key, label, eeaCat, osmCat, thresholdKey, field }) => ({
      g: 'Industrial Proximity', l: label, field,
      v: s => mergePair(readEea(s, eeaCat), readOsm(s, osmCat)),
      f: fmtPair,
      c: m => pairBucket(m, thresholdKey),
    }));
    const osmOnlyRows = OSM_ONLY_CATEGORIES.map(({ key, label, thresholdKey, field }) => ({
      g: 'Industrial Proximity', l: label, field,
      v: s => readOsm(s, key),
      f: fmtOsmOnly,
      c: o => singleBucket(o, thresholdKey),
    }));
    // Audit 2026-05-28: tag all Industrial Proximity rows FI-only.
    // EEA Industrial Reporting + OSM proximity are FI-ingested; NO
    // shows blanket "None within 30 km" which is just dataset absence,
    // not signal. Country filter at render hides them on NO compares.
    return [...eeaOnlyRows, ...overlapRows, ...osmOnlyRows].map(r => ({ ...r, countries: ['FI'] }));
  })()),
  // ── OSM Proximity Risks — 16 categories (Sprint C). Each cell shows
  // the nearest distance + a warn icon when within the category's
  // discretionary threshold. Color: warn → red, present-but-OK → amber,
  // not-found → green (rural sites often have None within 10km).
  ...((() => {
    // Phase 1+2: severity tier per row, plus DC-context distance thresholds
    // for the rows where distance does matter (hospital, police, military).
    // Schools/universities/govt/religious/water-towers/railway are 'info'
    // because they don't materially constrain a DC at typical rural Nordic
    // distances; flagging them amber created the "30 amber cells" noise.
    const proxRows = [
      // Public services group
      ['Public', 'Schools / daycare',     'school',             'info'],
      ['Public', 'Universities',          'university',         'info'],
      ['Public', 'Hospitals / medical',   'hospital',           'risk', { good: 15, warn: 30 }], // emergency response km
      ['Public', 'Police / EMS / Fire',   'emergency_services', 'risk', { good: 10, warn: 25 }], // fire response km
      ['Public', 'Govt / embassies',      'government',         'info'],
      // Restricted facilities group
      ['Restricted', 'Correctional',      'correctional',       'risk', { red: 1,  amber: 3  }], // km — far_is_good
      ['Restricted', 'Military',          'military',           'risk', { red: 2,  amber: 5  }],
      ['Restricted', 'Religious',         'religious',          'info'],
      // Industrial proximity merged into the unified "Industrial Proximity"
      // group above (Sprint E · Task 3) — these rows live there now.
      // Infrastructure group
      ['Infrastructure', 'Railway',       'railway',            'info'],
      ['Infrastructure', 'Water towers',  'water_tower',        'info'],
    ];
    return proxRows.map(([group, label, cat, severity, thr]) => ({
      g: group, l: label, s: severity, field: `proximity_${cat}`,
      countries: ['FI'],   // OSM proximity ingest is FI-only (audit 2026-05-28)
      v: s => {
        if (s.proximity_data_available === false) return null;
        const m = s[`proximity_${cat}_distance_m`];
        if (m == null) return { m: null };
        return { m, warn: !!s[`proximity_${cat}_warn`], count: s[`proximity_${cat}_count_2km`] || 0, name: s[`proximity_${cat}_name`] };
      },
      f: v => {
        if (v == null) return '—';
        if (v.m == null) return 'None within 30 km';
        const dist = v.m < 1000 ? `${v.m} m` : `${(v.m / 1000).toFixed(1)} km`;
        const cnt = v.count > 1 ? ` · ${v.count} within 2 km` : '';
        return `${dist}${cnt}${v.warn ? ' ⚠' : ''}`;
      },
      c: v => {
        if (v == null) return 'gray';
        if (v.m == null) return 'green';
        if (severity === 'info') return 'gray';
        if (!thr) return v.warn ? 'red' : 'amber';
        const km = v.m / 1000;
        // Two threshold styles:
        //   { good, warn }  — "near-is-good" (hospital response: ≤good=green)
        //   { red,  amber } — "far-is-good"  (military buffer:  <red=red)
        if (thr.good != null) {
          if (km <= thr.good) return 'green';
          if (km <= thr.warn) return 'amber';
          return 'red';
        }
        if (km < thr.red) return 'red';
        if (km < thr.amber) return 'amber';
        return 'green';
      },
    }));
  })()),
  // Cell tower proximity. Source = OpenCelliD when ingested, OSM Overpass
  // live otherwise. For staff connectivity / emergency comms backup.
  // Cell tower — informative only (DC runs on fiber; mobile is staff/backup
   // comms, never project-critical). Severity 'info' forces gray regardless
  // of distance.
  { g: 'Connectivity', l: 'Cell tower', field: 'nearest_cell_tower_m', s: 'info', countries: ['FI'],
    v: s => {
      if (s.cell_tower_data_available === false) return null;
      const m = s.nearest_cell_tower_m;
      if (m == null) return null;
      return { m, op: s.nearest_cell_tower_operator, ops: s.cell_tower_operators || [], n: s.cell_towers_within_5km };
    },
    f: v => {
      if (!v) return '—';
      const dist = v.m < 1000 ? `${v.m} m` : `${(v.m/1000).toFixed(1)} km`;
      const opsLabel = v.ops.length > 1 ? ` · ${v.ops.length} operators` : v.op ? ` · ${v.op}` : '';
      return `${dist}${opsLabel}`;
    },
    c: () => 'gray',
  },
  // Submarine cable landing — TeleGeography. For DC fiber-route diversity.
  // Distance is materially poor for inland sites; that's honest signal.
  { g: 'Connectivity', l: 'Subsea cable', field: 'subsea_landing_name', countries: ['FI'],
    v: s => {
      if (s.subsea_data_available === false) return null;
      if (!s.subsea_landing_name) return null;
      return { name: s.subsea_landing_name, km: s.subsea_distance_km, n: s.subsea_cable_count };
    },
    f: v => {
      if (!v) return '—';
      const km = v.km != null ? ` (${v.km.toFixed(0)} km)` : '';
      const cables = v.n ? ` · ${v.n} cable${v.n === 1 ? '' : 's'}` : '';
      return `${v.name}${km}${cables}`;
    },
    // Phase 2: <100 km optimal, <300 km standard for inland Norway/Finland,
    // >300 km is still informational not project-killing.
    c: v => {
      if (!v || v.km == null) return 'gray';
      if (v.km <= 100) return 'green';
      if (v.km <= 300) return 'amber';
      return 'red';
    },
  },
  // Broadband — distinguish "outside Traficom dataset scope" (currently
  // ships only Helsinki region, returns data_available=false) from real
  // "no coverage at this point". Sites in Pyhäjoki should NOT show "None"
  // as if we'd checked and found nothing.
  { g: 'Connectivity', l: 'Broadband', field: 'fi_broadband_1gbit', countries: ['FI'],
    v: s => {
      if (s.fi_broadband_data_available === false) return 'no_data';
      if (s.fi_broadband_1gbit === true)   return '1G';
      if (s.fi_broadband_100mbit === true) return '100M';
      if (s.fi_broadband_1gbit === false)  return 'None';
      return null;
    },
    f: v => v === '1G' ? '1 Gbit/s' : v === '100M' ? '100 Mbit/s'
          : v === 'None' ? 'None' : v === 'no_data' ? 'Outside dataset scope' : '—',
    // 'no_data' is informational (Traficom only ships 6-city scope) — gray,
    // not red. Real "None" within scope still red.
    c: v => v === '1G' ? 'green' : v === '100M' ? 'amber'
          : v === 'None' ? 'red' : 'gray',
  },
  // Phase 2: ≥30 ha = hyperscale-ready, ≥10 ha = enterprise-scale,
  // <10 ha = constrained.
  { g: 'Site', l: 'Area', field: 'total_area_ha', dir: 'higher',
    v: s => s.total_area_ha || s.areal_ha, f: v => v != null ? `${Number(v).toFixed(0)} ha` : '—',
    c: v => v == null ? 'gray' : v >= 30 ? 'green' : v >= 10 ? 'amber' : 'red' },
  // Phase 2: <2% minimal earthwork, ≤10% manageable CAPEX, >10% kills
  // big-pad designs.
  { g: 'Site', l: 'Slope', field: 'slope_pct', dir: 'lower',
    v: s => s.slope_pct, f: v => v != null ? `${Number(v).toFixed(0)}%` : '—',
    c: v => v == null ? 'gray' : v < 2 ? 'green' : v <= 10 ? 'amber' : 'red' },
  // Phaseable IT load — same formula as AnalysisPanel: 550 m²/MW × 60% util,
  // grid ceiling by voltage with distance penalty. Returns null if either
  // area or voltage is missing so the cell shows '—' rather than a guess.
  // Conservative voltage caps (mirrors dc_capacity_service.py). These are
  // typical max NEW connection MW per voltage class, NOT theoretical line
  // capacity — sanity-check audit required this be a hard cap because the
  // previous formula was producing fictional 954 MW assumptions.
  { g: 'Site', l: 'Phaseable MW (IT)', field: 'phaseable_mw_it', countries: ['FI'],
    v: s => {
      const areaHa = s.total_area_ha || s.areal_ha;
      const kv = s.nearest_grid_voltage_kv;
      const km = s.nearest_grid_distance_km;
      if (!areaHa || !kv) return null;
      const areaMw = (Number(areaHa) * 10000 * 0.6) / 550;
      const ceil = kv >= 400 ? 200 : kv >= 220 ? 100 : kv >= 110 ? 50 : kv >= 66 ? 20 : 10;
      const penalty = km == null ? 1 : km > 30 ? 0 : km > 15 ? 0.25 : km > 5 ? 0.5 : 1;
      const finalMw = Math.round(Math.min(areaMw, Math.round(ceil * penalty)));
      const limitedBy = areaMw < (ceil * penalty) ? 'area' : 'voltage';
      return { mw: finalMw, by: limitedBy };
    },
    f: v => v == null ? '—' : `~${v.mw} MW · ${v.by}`,
    c: v => v == null ? 'gray' : v.mw >= 100 ? 'green' : v.mw >= 30 ? 'amber' : 'red',
  },
  // Asemakaava — only for the 6 supported FI cities. Encodes both code and
  // permitted-status in v as an object so f/c can read both.
  { g: 'Site', l: 'Asemakaava (FI)', field: 'fi_municipal_zone_code', countries: ['FI'],
    v: s => s.fi_municipal_zone_code ? { code: s.fi_municipal_zone_code, permitted: s.fi_municipal_zone_permitted } : null,
    f: v => v ? `${v.code}${v.permitted === true ? ' ✓' : v.permitted === false ? ' ⚠' : ''}` : '—',
    c: v => !v ? 'gray' : v.permitted === true ? 'green' : v.permitted === false ? 'red' : 'amber',
  },
  // Building rights from asemakaava — when set, this is the cap on
  // developable footprint. Only present for the 6 supported cities.
  { g: 'Site', l: 'Building rights (FI)', field: 'fi_municipal_building_rights_m2', countries: ['FI'],
    v: s => s.fi_municipal_building_rights_m2 ?? null,
    f: v => v != null ? `${v.toLocaleString()} m²` : '—',
    c: v => v == null ? 'gray' : v >= 50000 ? 'green' : v >= 10000 ? 'amber' : 'red',
  },
  // Plan status — Voimassa = active (faster permitting), Vireillä = pending.
  { g: 'Site', l: 'Plan status (FI)', field: 'fi_municipal_plan_status', countries: ['FI'],
    v: s => s.fi_municipal_plan_status ?? null,
    f: v => v || '—',
    c: v => !v ? 'gray' : /voimassa/i.test(v) ? 'green' : /vireill/i.test(v) ? 'amber' : 'gray',
  },
  // Flood — split coastal/fluvial AND return-period. 1-in-100 is design
  // standard (red); 1-in-1000-only is climate stress (amber not red since
  // not blocking, just informational).
  { g: 'Environment', l: 'Flood risk', field: 'flood_zone',
    v: s => {
      if (s.flood_zone === false) return { tier: 'clear' };
      if (s.flood_zone !== true) return null;
      const types = s.fi_flood_types || [];
      const t = types.length === 2 ? 'river+sea' : s.fi_flood_sea ? 'sea' : s.fi_flood_river ? 'river' : 'in_zone';
      const tier = s.fi_flood_100yr ? '100yr' : s.fi_flood_1000yr ? '1000yr' : 'unknown';
      return { tier, t };
    },
    f: v => {
      if (!v) return '—';
      if (v.tier === 'clear') return 'Clear';
      const tName = v.t === 'river+sea' ? 'River + sea' : v.t === 'sea' ? 'Coastal' : v.t === 'river' ? 'River' : 'Zone';
      if (v.tier === '100yr') return `${tName} (100yr)`;
      if (v.tier === '1000yr') return `${tName} (1000yr stress)`;
      return tName;
    },
    c: v => !v ? 'gray' : v.tier === 'clear' ? 'green' : v.tier === '100yr' ? 'red' : v.tier === '1000yr' ? 'amber' : 'red',
  },
  // Heritage — count + nearest distance (m). Distance > 200m means EIA
  // Annex 2 trigger doesn't fire (per refined eia_trigger_service); reflect
  // that in cell color so analysts see the difference between "1 site at
  // 50m" (real concern) and "4 sites at 800m" (informational only).
  // Phase 2: <200m EIA Annex 2 trigger (red); 200m–1km consultation likely
  // (amber); >1km no impact (green).
  { g: 'Environment', l: 'Heritage', field: 'fi_cultural_heritage_count', countries: ['FI'],
    v: s => {
      const c = s.fi_cultural_heritage_count ?? s.cultural_monument_count;
      const m = s.fi_cultural_heritage_nearest_m;
      if (c == null) return null;
      return { count: c, m: m };
    },
    f: v => {
      if (v == null) return '—';
      if (v.count === 0) return 'None';
      if (v.m == null) return `${v.count} found`;
      const dist = v.m < 1000 ? `${Math.round(v.m)} m` : `${(v.m/1000).toFixed(1)} km`;
      return `${v.count} found · nearest ${dist}`;
    },
    c: v => {
      if (v == null) return 'gray';
      if (v.count === 0 || v.m == null) return 'green';
      if (v.m < 200)  return 'red';
      if (v.m < 1000) return 'amber';
      return 'green';
    },
  },
  // Phase 2: Natura 2000 EIA-trigger at <500 m. >5 km is fully clear.
  { g: 'Environment', l: 'Protected area', field: 'nearest_protected_distance_km',
    v: s => s.nearest_protected_distance_km,
    f: v => {
      if (v == null) return '—';
      if (v < 1) return `${Math.round(v * 1000)} m · check carefully`;
      if (v < 5) return `${v.toFixed(1)} km nearby`;
      if (v < 15) return `${v.toFixed(1)} km`;
      return 'None within 15 km';
    },
    c: v => v == null ? 'gray' : v < 0.5 ? 'red' : v < 5 ? 'amber' : 'green',
  },
  // Species — distinguish "checked, 0 found" from "API error" so '0' isn't
  // misleading. data_available=False means we couldn't reach Laji.fi or the
  // key is missing; show as gray "not checked" rather than green "0 found".
  { g: 'Environment', l: 'Species', field: 'fi_species', countries: ['FI'],
    v: s => {
      if (s.fi_species_data_available === false) return null;
      return s.red_listed_species_count;
    },
    f: v => v == null ? 'Not checked' : v === 0 ? 'None' : `${v} found`,
    c: v => v == null ? 'gray' : v === 0 ? 'green' : 'red',
  },
  // FI subsurface — populated for all FI sites via PG fast-path.
  { g: 'Environment', l: 'Groundwater (FI)', field: 'fi_groundwater_in_area', countries: ['FI'],
    v: s => s.fi_groundwater_in_area
      ? { in: true, klass: s.fi_groundwater_class, restr: s.fi_groundwater_restriction_level }
      : (s.fi_groundwater_data_available === false ? null : { in: false }),
    f: v => !v ? '—' : v.in ? `Class ${v.klass || '?'}` : 'Outside',
    c: v => !v ? 'gray' : !v.in ? 'green' : v.restr === 'very_high' ? 'red' : v.restr === 'high' ? 'red' : v.restr === 'moderate' ? 'amber' : 'amber',
  },
  { g: 'Environment', l: 'Acid sulfate (FI)', field: 'fi_acid_sulfate', countries: ['FI'],
    v: s => s.fi_acid_sulfate_in_area ? s.fi_acid_sulfate_probability : (s.fi_acid_sulfate_data_available === false ? null : 'outside'),
    f: v => !v ? '—' : v === 'outside' ? 'Outside' : v,
    c: v => !v ? 'gray' : v === 'outside' ? 'green' : (v || '').toLowerCase().includes('high') ? 'red' : (v || '').toLowerCase().includes('moderate') ? 'amber' : 'amber',
  },
  // Nuclear proximity — STUK emergency planning zones (5/20/100 km).
  { g: 'Hazards', l: 'Nuclear (FI)', field: 'nearest_nuclear_distance_km', countries: ['FI'],
    v: s => s.nearest_nuclear_distance_km != null ? { d: s.nearest_nuclear_distance_km, z: s.nuclear_risk_zone, n: s.nearest_nuclear_name } : null,
    f: v => v ? `${v.d.toFixed(0)} km` : '—',
    c: v => !v ? 'gray' : v.z === 'emergency' ? 'red' : v.z === 'extended' ? 'red' : v.z === 'awareness' ? 'amber' : 'green',
  },
  // EIA — Annex 1 (required) > Annex 2 (likely, ELY-keskus discretion) > none.
  // Surface the actual trigger reason inline so two sites both showing "Likely
  // (Annex 2)" can be distinguished — one might be flood, another heritage.
  { g: 'Permitting', l: 'EIA (YVA)', field: 'fi_eia_summary', countries: ['FI'],
    v: s => {
      if (!s.fi_eia_required && !s.fi_eia_likely && s.fi_eia_summary == null) return null;
      const verdict = s.fi_eia_required ? 'required' : s.fi_eia_likely ? 'likely' : 'clear';
      const triggers = s.fi_eia_triggers || [];
      // Strip the "Annex 2 §11" prefix to keep cell compact, take first reason
      const firstReason = triggers[0]?.reason?.replace(/^Annex \d.*?:\s*/, '') ?? null;
      return { verdict, triggerCount: triggers.length, firstReason };
    },
    f: v => {
      if (!v) return '—';
      if (v.verdict === 'clear') return 'No triggers';
      const label = v.verdict === 'required' ? 'Required' : 'Likely';
      if (!v.firstReason) return `${label} (Annex 2)`;
      const more = v.triggerCount > 1 ? ` +${v.triggerCount - 1} more` : '';
      return `${label}: ${v.firstReason}${more}`;
    },
    c: v => !v ? 'gray' : v.verdict === 'required' ? 'red' : v.verdict === 'likely' ? 'amber' : 'green',
  },
  // Residential proximity + noise verdict — permit-blocking for 24/7 ops.
  // showstopper / noise_study / clear come from noise_residential_service.
  { g: 'Permitting', l: 'Residential / noise', field: 'nearest_residential_distance_m', countries: ['FI'],
    v: s => s.nearest_residential_distance_m != null
      ? { d: s.nearest_residential_distance_m, flag: s.noise_flag }
      : null,
    f: v => {
      if (!v) return '—';
      const dist = v.d < 1000 ? `${Math.round(v.d)} m` : `${(v.d / 1000).toFixed(1)} km`;
      const tag = v.flag === 'showstopper' ? ' · unsiteable' : v.flag === 'noise_study' ? ' · study req.' : v.flag === 'clear' ? ' · clear' : '';
      return dist + tag;
    },
    c: v => !v ? 'gray' : v.flag === 'showstopper' ? 'red' : v.flag === 'noise_study' ? 'amber' : v.flag === 'clear' ? 'green' : 'gray',
  },
  { g: 'Access', l: 'Road distance', field: 'nearest_road_distance_km', dir: 'lower',
    // Frontend guard for the 10.0 km placeholder. Backend enrich-single
    // also drops the field at this value, but older saved enrichments
    // may still carry it — coerce to null at render so the row reads
    // "Not fetched · run Vegvesen check" instead of an identical 10 km
    // across all sites.
    v: s => {
      const v = s.nearest_road_distance_km;
      if (v == null || Math.abs(v - 10.0) < 0.01) return null;
      return v;
    },
    f: v => v != null ? `${Number(v).toFixed(1)} km` : 'Not fetched · run Vegvesen check',
    c: v => v == null ? 'gray' : v <= 0.5 ? 'green' : v <= 2 ? 'amber' : 'red' },
  { g: 'Climate', l: 'Free-cooling', field: 'free_cooling_days_yr', dir: 'higher',
    v: s => s.free_cooling_days_yr,
    // Bandified 2026-05-28. Underlying number is a crude proxy
    // (construction-season months × 30 + 40) — displaying "220 d/yr"
    // implied false precision. Bands convey the actual decision signal
    // without claiming the daily count is measured.
    f: v => v == null ? '—'
          : v >= 250 ? 'Strong (est.)'
          : v >= 200 ? 'Strong (est.)'
          : v >= 150 ? 'Moderate (est.)'
          :            'Weak (est.)',
    c: v => v == null ? 'gray' : v >= 200 ? 'green' : v >= 150 ? 'amber' : 'red' },
  { g: 'Climate', l: 'Design temp', s: 'info', field: 'design_temp_min_c', countries: ['FI'], v: s => s.design_temp_min_c != null ? `${s.design_temp_min_c}°C / +${s.design_temp_max_c}°C` : null, f: v => v || '—', c: () => 'gray' },
  // Water Source/Type are descriptive — distance is the actual risk metric.
  { g: 'Water', l: 'Source', s: 'info', field: 'river_name', v: s => s.river_name || s.lake_name, f: v => v || '—', c: v => v ? 'green' : 'gray' },
  // Saltwater needs different cooling kit than freshwater. Backend uses
  // saltwater | freshwater | elv | innsjø; normalise to a clean 3-way
  // category for display.
  { g: 'Water', l: 'Type', s: 'info', field: 'water_source_type',
    v: s => {
      const t = s.water_source_type;
      if (!t) return null;
      if (t === 'saltwater') return 'sea';
      if (t === 'freshwater' || t === 'elv' || t === 'innsjø') return 'fresh';
      return null;
    },
    f: v => v === 'sea' ? 'Sea / Fjord' : v === 'fresh' ? 'Freshwater' : '—',
    c: v => !v ? 'gray' : v === 'fresh' ? 'green' : 'amber',
  },
  { g: 'Water', l: 'Distance', field: 'water_distance_km', dir: 'lower', v: s => s.water_distance_km, f: v => v != null ? `${Number(v).toFixed(1)} km` : '—', c: v => v == null ? 'gray' : v <= 2 ? 'green' : v <= 5 ? 'amber' : 'red' },
  // DH and Hydropower = opportunity tier — close/large = green; far/none =
  // gray (not red), because absence of waste-heat offtake or hydro PPA is
  // not a project loss, just a missed bonus.
  { g: 'District Heating', l: 'DH plant distance', s: 'opportunity', field: 'nearest_dh_plant_distance_km', countries: ['FI'], v: s => s.nearest_dh_plant_distance_km, f: v => v != null ? `${Number(v).toFixed(0)} km` : '—', c: v => v == null ? 'gray' : v <= 5 ? 'green' : v <= 15 ? 'amber' : 'red' },
  { g: 'District Heating', l: 'DH capacity', s: 'opportunity', field: 'nearest_dh_plant_mw', countries: ['FI'], v: s => s.nearest_dh_plant_mw, f: v => v != null ? `${v} MW` : '—', c: v => v == null ? 'gray' : v >= 50 ? 'green' : v >= 10 ? 'amber' : 'red' },
  // Hydropower group removed 2026-05-28 — depended on
  // data_layers.no_hydropower_plants which isn't ingested on prod yet;
  // row was rendering "Not checked (data unavailable)" on every NO site.
  // Restore when ingest_no_hydropower.py has been run.

  // Connectivity — fibre name + carriers + diversity (NO; the IXP-distance
  // row above stays universal). Fibre meet-point identity matters for
  // procurement: "NIX Mo i Rana, 3 carriers, single-path" is meaningfully
  // different from "NIX Stavanger, 12 carriers, dual-path".
  { g: 'Connectivity', l: 'Fibre meet point', s: 'info', field: 'nearest_ixp_name', countries: ['NO'],
    v: s => s.nearest_ixp_name ? {
      name: s.nearest_ixp_name,
      city: s.nearest_ixp_city,
      carriers: s.carriers_estimated,
      diversity: s.fiber_diversity,
    } : null,
    f: v => {
      if (!v) return '—';
      const city = v.city ? ` (${v.city})` : '';
      const div  = v.diversity ? ` · ${v.diversity}` : '';
      const cars = v.carriers != null ? ` · ${v.carriers} carriers` : '';
      return `${v.name}${city}${div}${cars}`;
    },
    c: v => !v ? 'gray' : v.diversity === 'strong' ? 'green' : v.diversity === 'dual' ? 'amber' : 'gray',
  },

  // ── NO-only rows added 2026-05-28 ─────────────────────────────────
  // Sourced from DataView FIELD_MAP — fields that the NO enrichment
  // pipeline actually populates. Cell-format/colors mirror the equivalent
  // single-site analysis panel so saved sites read the same as live ones.

  // Power — price zone (NO1–NO5). Helgeland sits in NO4 (Nord-Norge),
  // historically the cheapest zone. Surfacing the zone here lets analysts
  // see why one site's LCOH is lower than another's at a glance.
  { g: 'Power', l: 'Price zone', s: 'info', field: 'nearest_grid_price_zone', countries: ['NO'],
    v: s => s.nearest_grid_price_zone || null,
    f: v => v || '—',
    c: () => 'gray',
  },
  // Power economics — live NordPool spot. 30-day rolling avg is the
  // number used in LCOH; today's spot is the volatility check.
  { g: 'Power Economics', l: '30-day spot avg', field: 'spot_30d_avg_nok_mwh', countries: ['NO'], dir: 'lower',
    v: s => s.spot_30d_avg_nok_mwh,
    f: v => v != null ? `${Number(v).toFixed(0)} NOK/MWh` : '—',
    // <300 great, <500 fine, <700 ok, >700 expensive (NO4 has been ~150–300 NOK/MWh)
    c: v => v == null ? 'gray' : v < 300 ? 'green' : v < 500 ? 'amber' : 'red',
  },
  { g: 'Power Economics', l: 'Spot today', s: 'info', field: 'spot_today_nok_mwh', countries: ['NO'],
    v: s => s.spot_today_nok_mwh,
    f: v => v != null ? `${Number(v).toFixed(0)} NOK/MWh` : '—',
    c: () => 'gray',
  },

  // Site — realistic capacity from site_capacity service. This is the
  // headline MW we calculated (buildable area × density tier, then capped
  // by voltage class). Distinct from the heuristic "Phaseable MW (IT)"
  // row above — that one is a quick estimate, this is the actual one we
  // ship in the analysis panel and rank by.
  { g: 'Site', l: 'Realistic capacity', field: 'max_practical_mw', countries: ['NO'], dir: 'higher',
    v: s => s.max_practical_mw,
    f: v => v != null ? `${Math.round(v)} MW` : '—',
    c: v => v == null ? 'gray' : v >= 100 ? 'green' : v >= 30 ? 'amber' : 'red',
  },
  { g: 'Site', l: 'Net buildable', field: 'capacity_effective_buildable_ha', countries: ['NO'], dir: 'higher',
    v: s => s.capacity_effective_buildable_ha,
    f: v => v != null ? `${Number(v).toFixed(1)} ha` : '—',
    c: v => v == null ? 'gray' : v >= 20 ? 'green' : v >= 8 ? 'amber' : 'red',
  },
  { g: 'Site', l: 'Elevation', s: 'info', field: 'elevation_m', countries: ['NO'],
    v: s => s.elevation_m,
    f: v => v != null ? `${Math.round(v)} m a.s.l.` : '—',
    // >600 m is permitting-friction territory (mountainous), shade amber
    c: v => v == null ? 'gray' : v > 600 ? 'amber' : 'gray',
  },

  // Planning — DIBK zoning + AR5 land cover. DIBK is the legally-binding
  // zoning plan; when it's missing (most rural NO), AR5 land cover is
  // the fallback signal.
  { g: 'Planning', l: 'DIBK zone', field: 'zone_code', countries: ['NO'],
    v: s => s.zone_code ? { code: s.zone_code, name: s.zone_name, suit: s.zone_suitability } : null,
    // No DIBK polygon is the norm in rural NO (Helgeland is ~45% DIBK-covered).
    // Tell the analyst that explicitly — AR5 land-cover row below covers
    // the fallback zoning signal, so this isn't actually missing info.
    f: v => !v ? 'Not in DIBK · see AR5 below' : `${v.code}${v.name ? ' · ' + v.name : ''}`,
    // zone_suitability is 0–1; >0.7 industrial-friendly, <0.3 residential/conservation
    c: v => !v ? 'gray' : v.suit == null ? 'gray' : v.suit >= 0.7 ? 'green' : v.suit >= 0.4 ? 'amber' : 'red',
  },
  { g: 'Planning', l: 'Land cover (AR5)', field: 'ar5_code', countries: ['NO'],
    v: s => s.ar5_code != null ? { code: Number(s.ar5_code), name: s.ar5_name_en || s.ar5_name } : null,
    f: v => !v ? '—' : v.name || `Code ${v.code}`,
    // 11=Bebygd (built-up), 12=Samferdsel (transport) — hard reds
    // 30=Skog (forest), 50=Åpen fastmark (open ground) — green, buildable
    // 23/22=Dyrka/innmark (agricultural) — amber, often protected
    c: v => {
      if (!v) return 'gray';
      if (v.code === 11 || v.code === 12) return 'red';
      if (v.code === 30 || v.code === 50) return 'green';
      if (v.code === 22 || v.code === 23) return 'amber';
      return 'gray';
    },
  },

  // Environment NO — quick clay (kvikkleire) is the local hazard equivalent
  // of acid sulfate; salmon rivers + cultural monuments are NO-specific.
  // Label fix 2026-05-28: was "Hard stop" which contradicted reality
  // (these parcels were saved as viable projects, so they didn't actually
  // hard-stop). The flag means NGU kvikkleireskredfare risk points exist
  // — review-required, not project-killing. Real hard stops should be
  // hit at the SSE rejection stage and never reach saved projects.
  { g: 'Environment', l: 'Quick clay', field: 'kvikkleire_hard_stop', countries: ['NO'],
    v: s => {
      if (s.kvikkleire_hard_stop === true) return 'risk';
      if (s.kvikkleire && s.kvikkleire !== 'Ingen') return 'flag';
      if (s.kvikkleire === 'Ingen' || s.kvikkleire_hard_stop === false) return 'clear';
      return null;
    },
    f: v => v === 'risk' ? 'Risk points — review' : v === 'flag' ? 'Flagged' : v === 'clear' ? 'Clear' : '—',
    c: v => v === 'risk' ? 'amber' : v === 'flag' ? 'amber' : v === 'clear' ? 'green' : 'gray',
  },
  { g: 'Environment', l: 'Salmon river (≤500 m)', field: 'is_salmon_river', countries: ['NO'],
    v: s => s.is_salmon_river === true ? 'yes' : s.is_salmon_river === false ? 'no' : null,
    f: v => v === 'yes' ? 'Within 500 m' : v === 'no' ? 'Clear' : '—',
    c: v => v === 'yes' ? 'red' : v === 'no' ? 'green' : 'gray',
  },
  { g: 'Environment', l: 'Cultural monument', field: 'har_kulturminne', countries: ['NO'],
    v: s => s.har_kulturminne === true ? 'yes' : s.har_kulturminne === false ? 'no' : null,
    f: v => v === 'yes' ? 'Nearby (Riksantikvaren)' : v === 'no' ? 'Clear' : '—',
    c: v => v === 'yes' ? 'red' : v === 'no' ? 'green' : 'gray',
  },

  // Port row removed 2026-05-28 — relevant for hydrogen export logistics,
  // not for the data-centre projects this user is currently saving.
  // If we add a use_case filter pattern later, re-add and tag with
  // use_cases: ['hydrogen'].

  // Grid CAPEX — what the LCOH service estimates for the substation
  // upgrade + spur. Lets two otherwise-equal sites be ranked by the
  // actual hard cost difference rather than just distance proxy.
  { g: 'Power', l: 'Grid CAPEX (est.)', field: 'grid_capex_mnok', countries: ['NO'], dir: 'lower',
    v: s => s.grid_capex_mnok,
    f: v => v != null ? `${Number(v).toFixed(1)} MNOK` : '—',
    c: v => v == null ? 'gray' : v < 10 ? 'green' : v < 30 ? 'amber' : 'red',
  },

  // LCOH — hydrogen only. Tagged use_cases:['hydrogen'] so it stays hidden
  // when comparing data-centre projects (LCOH $/kg H₂ is meaningless for DC).
  { g: 'Hydrogen Economics', l: 'LCOH', field: 'lcoh_usd_kg', countries: ['NO'], use_cases: ['hydrogen'],
    v: s => s.lcoh_usd_kg,
    f: v => v != null ? `$${Number(v).toFixed(2)}/kg H₂` : '—',
    // <3 cost-competitive, <5 workable, >5 hard to bank
    c: v => v == null ? 'gray' : v < 3 ? 'green' : v < 5 ? 'amber' : 'red',
  },

  // ── Data Centre Economics (added 2026-05-28) ──────────────────────
  // Replaces the LCOH row for DC use-case. All derived from already-saved
  // enrichment — no new backend lookups needed.

  // Annual energy cost for the IT load the user actually requested
  // (project.use_case_params.plant_mw or 20 MW default), NOT the site's
  // theoretical max capacity. Audit 2026-05-28: was computing on
  // max_practical_mw, which made a 110 MW site look 5× more expensive
  // than it actually would be for a 20 MW project — penalizing the
  // best site for being too big.
  // Formula: requested_mw × 0.85 utilisation × 8760 hrs × NOK/MWh ÷ 1M
  { g: 'Data Centre Economics', l: 'Annual energy cost (at requested load)',
    field: 'annual_energy_cost_mnok',
    countries: ['NO'], use_cases: ['data_center'], dir: 'lower',
    v: s => {
      const requested = (s._project?.use_case_params?.plant_mw) ?? s.plant_mw ?? 20;
      const price = s.spot_30d_avg_nok_mwh ?? s.electricity_price_nok_mwh;
      if (price == null) return null;
      return requested * 0.85 * 8760 * price / 1_000_000;   // MNOK/yr at REQUESTED load
    },
    f: (v, s) => {
      if (v == null) return '—';
      const requested = (s._project?.use_case_params?.plant_mw) ?? s.plant_mw ?? 20;
      return `~${Math.round(v)} MNOK/yr @ ${requested} MW`;
    },
    c: v => v == null ? 'gray' : v < 50 ? 'green' : v < 150 ? 'amber' : 'red',
  },

  // Indicative CAPEX range: plant equipment scales with MW (DC ≈ 50 MNOK/MW
  // for shell+M&E at hyperscale economics, lower for colo). Grid CAPEX is
  // additive. ±25% band; mirrors the SummaryView calc.
  { g: 'Data Centre Economics', l: 'Site CAPEX range (at requested load)',
    field: 'site_capex_range_mnok',
    countries: ['NO'], use_cases: ['data_center'], dir: 'lower',
    v: s => {
      // Scope to the requested IT load, not max site capacity. Was
      // using max_practical_mw which inflated CAPEX 5× for over-sized
      // sites and made the best site look most expensive.
      const requested = (s._project?.use_case_params?.plant_mw) ?? s.plant_mw ?? 20;
      // 50 MNOK/MW = enterprise-tier shell + M&E (Bulk N01 / Lefdal).
      // Hyperscale economics are 30–40 MNOK/MW; mentally discount ~25%
      // if scoping that tier.
      const plant = requested * 50;
      const grid = s.grid_capex_mnok || 0;
      const total = plant + grid;
      return { low: Math.round(total * 0.75), high: Math.round(total * 1.25), total, mw: requested };
    },
    f: v => v == null ? '—' : `${v.low}–${v.high} MNOK @ ${v.mw} MW`,
    c: v => v == null ? 'gray' : v.total < 500 ? 'green' : v.total < 2000 ? 'amber' : 'red',
  },

  // Headroom vs requested IT load (each project carries use_case + the
  // saved screening's plant_mw context). Positive = site can serve the
  // target with room to grow; negative = under-sized.
  { g: 'Data Centre Economics', l: 'Capacity headroom', field: 'capacity_headroom_mw',
    countries: ['NO'], use_cases: ['data_center'], dir: 'higher',
    v: s => {
      const realistic = s.max_practical_mw;
      // Saved project carries the IT load it was screened for in
      // project.use_case_params. Fall back to 20 MW (the free-tier default).
      const requested = (s._project?.use_case_params?.plant_mw)
                       ?? s.plant_mw ?? 20;
      if (realistic == null) return null;
      return { realistic, requested, headroom: realistic - requested };
    },
    f: v => v == null ? '—' :
      `${Math.round(v.realistic)} MW vs ${v.requested} MW · ${v.headroom >= 0 ? '+' : ''}${Math.round(v.headroom)} MW`,
    c: v => v == null ? 'gray' :
      v.headroom < 0 ? 'red' : v.headroom < v.requested * 0.5 ? 'amber' : 'green',
  },
];

const cellBg = { green: 'rgba(34,197,94,0.08)', amber: 'rgba(234,179,8,0.08)', red: 'rgba(239,68,68,0.08)', gray: 'transparent' };
const cellFg = { green: '#86efac', amber: '#fde047', red: '#fca5a5', gray: '#64748b' };

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_TOKEN || '';
const thumbUrl = (lat, lon) =>
  `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/pin-s+ef4444(${lon},${lat})/${lon},${lat},13,0/200x100@2x?access_token=${MAPBOX_TOKEN}`;

const STAGES = [
  { key: 'candidate', label: 'Candidate' },
  { key: 'screening', label: 'Screening' },
  { key: 'land_contact', label: 'Land Contact' },
  { key: 'option_signed', label: 'Option Signed' },
  { key: 'grid_applied', label: 'Grid Applied' },
  { key: 'permitting', label: 'Permitting' },
  { key: 'investment', label: 'Investment' },
  { key: 'active', label: 'Active' },
];

// scoreColor — proxy to tierForIndex so the legacy callers keep working
// with the unified Strong / Suitable / Conditional / Weak palette.
function scoreColor(pct) {
  return tierForIndex(pct).color;
}

function daysSince(iso) {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  return d === 0 ? 'today' : `${d}d ago`;
}

// ── Add Site Form ───────────────────────────────────────────────────
function AddSiteForm({ onAdd, freeCapHit = false }) {
  const { user: auth0User } = useAuth0();
  const [open, setOpen] = useState(false);
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!lat || !lon) return;
    setSaving(true);
    await onAdd({
      lat: parseFloat(lat), lon: parseFloat(lon),
      dd_label: label || `Site ${parseFloat(lat).toFixed(3)}, ${parseFloat(lon).toFixed(3)}`,
      country: 'FI', use_case: 'data_center',
    });
    setLat(''); setLon(''); setLabel('');
    setSaving(false);
    setOpen(false);
  };

  if (!open) {
    if (freeCapHit) {
      return (
        <button
          className="pp-add-btn"
          onClick={() => openCalendly(auth0User)}
          title="Free tier: 3 of 3 projects used — Book a call for unlimited"
          style={{ opacity: 0.6 }}
        >
          + Add site 🔒
        </button>
      );
    }
    return <button className="pp-add-btn" onClick={() => setOpen(true)}>+ Add site</button>;
  }

  return (
    <div className="pp-add-form">
      <input placeholder="Latitude" value={lat} onChange={e => setLat(e.target.value)} />
      <input placeholder="Longitude" value={lon} onChange={e => setLon(e.target.value)} />
      <input placeholder="Label (optional)" value={label} onChange={e => setLabel(e.target.value)} />
      <div className="pp-add-form-actions">
        <button onClick={handleAdd} disabled={saving || !lat || !lon} className="pp-add-submit">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={() => setOpen(false)} className="pp-add-cancel">Cancel</button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════
export default function ProjectsPage() {
  const { projects, activeProjects, screeningProjects, projectCount, loading, error,
          saveProject, updateProject, deleteProject, reEnrich, isEnriching, enrichingCount } = useProjects();
  const { isPremium } = useIsPremium();
  const freeCapHit = !isPremium && projectCount >= FREE_TIER_PROJECT_CAP;

  const [tab, setTab] = useState('compare'); // 'compare' | 'pipeline' | 'timeline'

  // AI analysis state
  const [aiData, setAiData] = useState({}); // project_id → { feasibility, queue, aiAnalysis }
  const [aiLoading, setAiLoading] = useState(new Set());

  const runAiAnalysis = useCallback(async (project) => {
    const e = project.enrichment || {};
    setAiLoading(prev => new Set(prev).add(project.id));
    try {
      // Run grid feasibility + DD agents in parallel
      const apiBase = import.meta.env?.VITE_API_URL || '';
      const [gridResult, ddResp] = await Promise.all([
        fetchGridFeasibility(project.lat, project.lon, 20, e.nearest_grid_voltage_kv || 66),
        fetch(`${apiBase}/api/v1/agents/due-diligence`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lat: project.lat,
            lng: project.lon,
            country: project.country || 'FI',
            project_type: project.use_case || 'data_center',
            requested_mw: 20,
          }),
        }).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      setAiData(prev => ({ ...prev, [project.id]: { ...gridResult, dueDiligence: ddResp } }));
    } catch (err) {
      console.error('AI analysis failed:', err);
      setAiData(prev => ({ ...prev, [project.id]: { error: err.message } }));
    }
    setAiLoading(prev => { const n = new Set(prev); n.delete(project.id); return n; });
  }, []);

  // Detect projects with sparse enrichment data. Generic keys apply to
  // both countries; FI-specific keys catch saved sites that predate the
  // adoption of nuclear / groundwater / acid sulfate / EIA enrichment.
  const isSparse = useCallback((project) => {
    const e = project.enrichment || {};
    const keys = ['slope_pct', 'flood_zone', 'nearest_ixp_distance_km', 'nearest_road_distance_km', 'free_cooling_days_yr', 'water_distance_km'];
    if (project.country === 'FI') {
      keys.push(
        'nearest_nuclear_distance_km',
        'fi_groundwater_data_available',
        'fi_acid_sulfate_data_available',
        'fi_eia_summary',
      );
    }
    // Missing 3+ of these key fields → needs enrichment
    const missing = keys.filter(k => e[k] == null).length;
    return missing >= 3;
  }, []);

  const sparseProjects = screeningProjects.filter(isSparse);

  const handleEnrichOne = useCallback(async (projectId) => {
    await reEnrich(projectId);
  }, [reEnrich]);

  const handleEnrichAll = useCallback(async () => {
    for (const p of sparseProjects) {
      if (!isEnriching(p.id)) {
        await reEnrich(p.id);
      }
    }
  }, [sparseProjects, isEnriching, reEnrich]);

  // Force re-enrichment of every saved site, regardless of sparseness.
  // Useful when new enrichment fields ship — sites that were "complete"
  // by the old definition still need a refresh to get the new data.
  const handleRefreshAll = useCallback(async () => {
    if (!confirm(`Re-fetch enrichment data for all ${screeningProjects.length} saved sites? This may take a few minutes.`)) return;
    for (const p of screeningProjects) {
      if (!isEnriching(p.id)) {
        await reEnrich(p.id);
      }
    }
  }, [screeningProjects, isEnriching, reEnrich]);

  // Grid queue auto-fetch — surfaces TSO queue MW within 15 km of the
  // *nearest substation* for every saved site (not the site itself —
  // queue projects connect to substations, so a site 4 km from its
  // substation would otherwise miss queue rows that are well within
  // the substation's 15 km radius). Falls back to site coords when
  // we can't resolve a substation. Cached by project.id.
  const apiBase = import.meta.env?.VITE_API_URL || '';
  const [gridQueueBySite, setGridQueueBySite] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pending = screeningProjects.filter(p =>
        p.lat != null && p.lon != null && !(p.id in gridQueueBySite)
      );
      if (pending.length === 0) return;
      const results = await Promise.all(pending.map(async p => {
        try {
          // 1. Resolve substation coords. Prefer those baked into
          //    enrichment; if missing (older saved sites), call
          //    /sites/nearest-grid as a backfill.
          let centerLat = p.enrichment?.nearest_grid_lat;
          let centerLon = p.enrichment?.nearest_grid_lon;
          if (centerLat == null || centerLon == null) {
            try {
              const ng = await fetch(
                `${apiBase}/api/v1/sites/nearest-grid?lat=${p.lat}&lng=${p.lon}&country=${p.country || 'FI'}`
              );
              if (ng.ok) {
                const nb = await ng.json();
                centerLat = nb.lat;
                centerLon = nb.lon;
              }
            } catch {}
          }
          // 2. Final fallback: site coords (better than nothing).
          if (centerLat == null || centerLon == null) {
            centerLat = p.lat; centerLon = p.lon;
          }

          const qs = new URLSearchParams({
            lat: centerLat, lng: centerLon, radius_km: 15,
          }).toString();
          const API_BASE = import.meta.env.VITE_API_URL || '';
          const r = await fetch(`${API_BASE}/api/v1/grid-feasibility/queue?${qs}`);
          if (!r.ok) return [p.id, null];
          return [p.id, await r.json()];
        } catch {
          return [p.id, null];
        }
      }));
      if (cancelled) return;
      setGridQueueBySite(prev => {
        const next = { ...prev };
        for (const [id, d] of results) next[id] = d;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [screeningProjects]);  // eslint-disable-line react-hooks/exhaustive-deps

  // For comparison, use enrichment data from screening projects, with the
  // grid-queue fetch result merged in as `_grid_queue` for the CRITERIA row.
  // Preserve undefined (fetch hasn't run) vs null (fetch ran + failed)
  // vs object (fetch succeeded) so the queue row can tell the analyst
  // the difference between "queue clear" and "we don't know yet".
  const compareSites = useMemo(
    () => screeningProjects.map(p => ({
      ...p.enrichment,
      _project: p,
      _grid_queue: p.id in gridQueueBySite ? gridQueueBySite[p.id] : undefined,
    })),
    [screeningProjects, gridQueueBySite],
  );
  // Country-aware row filter (audit 2026-05-28). When all sites being
  // compared share the same country, hide rows tagged for the other
  // country — saves ~30 useless "Outside dataset scope" / "—" cells on
  // the NO Compare view that's nothing but noise. Mixed-country compares
  // (rare) fall through to "show everything tagged for any country
  // present" so neither half is hidden.
  const compareCountries = new Set(compareSites.map(s => s._project?.country).filter(Boolean));
  // use_case filter (audit 2026-05-28). When all sites in the compare
  // share a use_case, hide rows that don't apply (e.g. LCOH on DC sites,
  // DC economics on hydrogen sites). Default use_case = 'data_center'
  // since that's what the free-tier defaults to.
  const compareUseCases = new Set(
    compareSites.map(s => s._project?.use_case || 'data_center').filter(Boolean)
  );
  const rowAppliesToCompare = useCallback((cr) => {
    if (cr.countries && compareCountries.size > 0
        && !cr.countries.some(c => compareCountries.has(c))) return false;
    if (cr.use_cases && compareUseCases.size > 0
        && !cr.use_cases.some(u => compareUseCases.has(u))) return false;
    return true;
  }, [compareCountries, compareUseCases]);
  const visibleCriteria = CRITERIA.filter(rowAppliesToCompare);

  // Precompute every row's cells once per render — needed for best-in-row
  // highlight, identical-row collapse, AND verdict-row generation (which
  // reuses the same per-cell scoring). Each rendered cell carries
  // display text, color tier, raw value, comparison number, and whether
  // it's the unique best in its row.
  const computedRows = useMemo(() => visibleCriteria.map(cr => {
    const cells = compareSites.map(s => {
      const raw = cr.v(s);
      const display = (raw == null && cr.field)
        ? nullDisplay(s, cr.field)
        : (cr.f ? cr.f(raw, s) : (raw != null ? String(raw) : '—'));
      const computedC = cr.c ? cr.c(raw) : 'gray';
      const c = cr.s === 'info' ? 'gray'
              : (cr.s === 'opportunity' && computedC === 'red') ? 'gray'
              : computedC;
      return { siteId: s._project.id, raw, display, color: c, cmpNum: extractCompareNum(raw) };
    });
    let bestIdx = null;
    if (cr.dir) {
      // Band-aware best-in-row: highlight only when sites fall in
      // DIFFERENT color bands (per the row's c() function). Two sites
      // both in the 'green' band ≠ "one is the best" — that's noise,
      // not signal. Within-band differences (IXP 89km vs 94km, both
      // amber) get no highlight; cross-band differences (one green, one
      // red) get the green-band cell highlighted.
      const bandColors = cells.map(c => c.color).filter(c => c !== 'gray');
      const uniqueBands = new Set(bandColors);
      if (uniqueBands.size >= 2) {
        bestIdx = pickBestIndex(cells.map(c => c.cmpNum), cr.dir);
      }
    }
    cells.forEach((c, i) => { c.isBest = i === bestIdx; });
    // Identical = every cell's display string matches. Don't compare
    // raw values (could be objects with reference inequality); the
    // displayed text is what the user actually sees.
    const displays = cells.map(c => (typeof c.display === 'string' ? c.display : null));
    const allIdentical = displays.length > 1
      && displays.every(d => d != null && d === displays[0]);
    return { cr, cells, allIdentical };
  }), [visibleCriteria, compareSites]);

  // Group splits: rows that differ between sites (main table) vs rows
  // identical across all sites (collapsed under expander, since they
  // carry no decision value).
  const differingRows = computedRows.filter(r => !r.allIdentical);
  const identicalRows = computedRows.filter(r => r.allIdentical);
  const groups = [...new Set(differingRows.map(r => r.cr.g))];
  const [showShared, setShowShared] = useState(false);

  // Verdict row inputs — for each site, find the rows where it has the
  // unique best (→ "Best for") and the rows where it's the worst
  // (→ "Watch out"). Only considers rows tagged with dir so info /
  // categorical rows don't generate noise. Also flags hard constraints
  // (e.g. capacity headroom < 0 = site is undersized for the request)
  // even when they're not the worst across compared sites — a 75-score
  // site that physically can't fit the requested load shouldn't get
  // a clean "Watch out: —".
  const verdictPerSite = useMemo(() => compareSites.map((s, siteIdx) => {
    const bestRows = [];
    const worstRows = [];
    for (const { cr, cells } of computedRows) {
      if (!cr.dir) continue;
      // Differentiator fires only when band-aware isBest is set
      // (computedRows already gates that on cross-band difference).
      // For worst we also require band difference — same check applied
      // by checking uniqueBands of non-gray colors.
      const bandColors = cells.map(c => c.color).filter(c => c !== 'gray');
      if (new Set(bandColors).size < 2) continue;
      if (cells[siteIdx]?.isBest) bestRows.push({ label: cr.l, display: cells[siteIdx].display });
      const nums = cells.map(c => c.cmpNum);
      const worstIdx = pickBestIndex(nums, cr.dir === 'lower' ? 'higher' : 'lower');
      if (worstIdx === siteIdx) worstRows.push({ label: cr.l, display: cells[siteIdx].display });
    }
    // Hard constraint: capacity headroom < 0 = site can't fit requested load.
    // Surface this regardless of how it compares to other sites.
    const requested = s._project?.use_case_params?.plant_mw ?? s.plant_mw ?? 20;
    const realistic = s.max_practical_mw;
    const undersized = realistic != null && realistic < requested;
    if (undersized) {
      worstRows.unshift({
        label: `Undersized for ${requested} MW`,
        display: `${Math.round(realistic)} MW capacity`,
        priority: 'hard',
      });
    }
    return { site: s, bestRows, worstRows, undersized };
  }), [compareSites, computedRows]);

  const handleStageChange = useCallback(async (project, newStage) => {
    if (newStage === 'dropped') {
      await deleteProject(project.id);
    } else {
      await updateProject(project.id, { stage: newStage });
    }
  }, [updateProject, deleteProject]);

  const exportToExcel = useCallback(() => {
    if (!screeningProjects.length) return;
    const rows = [];
    // Header row
    rows.push(['Criterion', ...screeningProjects.map(p => p.site_label)]);
    // Readiness scores
    rows.push(['SEIDR Index', ...screeningProjects.map(p => p.readiness_score != null ? `${Math.round(p.readiness_score)}` : '—')]);
    rows.push(['SEIDR Tier',  ...screeningProjects.map(p => p.readiness_score != null ? tierForIndex(p.readiness_score).tier : '—')]);
    rows.push([]);
    // Criteria comparison
    for (const cr of CRITERIA) {
      const vals = compareSites.map(s => {
        const raw = cr.v(s);
        return cr.f ? cr.f(raw) : (raw ?? '—');
      });
      rows.push([`${cr.g} — ${cr.l}`, ...vals]);
    }
    // AI analysis results
    rows.push([]);
    rows.push(['AI ANALYSIS']);
    for (const p of screeningProjects) {
      const ai = aiData[p.id];
      const feas = ai?.feasibility;
      const dd = ai?.dueDiligence;
      const col = screeningProjects.indexOf(p) + 1;
      if (!rows[rows.length - 1]) rows.push([]);
      if (feas) {
        if (!rows.find(r => r[0] === 'Connection realism')) {
          rows.push(['Connection realism', ...screeningProjects.map(() => '')]);
          rows.push(['5-year probability', ...screeningProjects.map(() => '')]);
          rows.push(['Est. years to power', ...screeningProjects.map(() => '')]);
        }
        const crRow = rows.find(r => r[0] === 'Connection realism');
        const p5Row = rows.find(r => r[0] === '5-year probability');
        const etRow = rows.find(r => r[0] === 'Est. years to power');
        if (crRow) crRow[col] = feas.connection_realism?.band || '—';
        if (p5Row) p5Row[col] = feas.time_to_power?.probability_5y != null ? `${Math.round(feas.time_to_power.probability_5y * 100)}%` : '—';
        if (etRow) etRow[col] = feas.time_to_power?.estimated_years != null ? `${feas.time_to_power.estimated_years.toFixed(1)} yr` : '—';
      }
      if (dd) {
        if (!rows.find(r => r[0] === 'DD Recommendation')) {
          rows.push([]);
          rows.push(['DUE DILIGENCE']);
          rows.push(['DD Recommendation', ...screeningProjects.map(() => '')]);
          rows.push(['DD Readiness', ...screeningProjects.map(() => '')]);
          const sc = dd.scorecard || {};
          for (const k of Object.keys(sc).filter(k => k !== 'overall_readiness')) {
            rows.push([`DD — ${k.replace(/_/g, ' ')}`, ...screeningProjects.map(() => '')]);
          }
        }
        const ddRec = rows.find(r => r[0] === 'DD Recommendation');
        const ddReady = rows.find(r => r[0] === 'DD Readiness');
        if (ddRec) ddRec[col] = (dd.recommendation || '').replace('_', ' ');
        if (ddReady) ddReady[col] = dd.scorecard?.overall_readiness != null ? `${Math.round(dd.scorecard.overall_readiness * 100)}%` : '—';
        for (const [k, v] of Object.entries(dd.scorecard || {})) {
          if (k === 'overall_readiness') continue;
          const row = rows.find(r => r[0] === `DD — ${k.replace(/_/g, ' ')}`);
          if (row) row[col] = v != null ? `${Math.round(v * 100)}%` : '—';
        }
      }
    }
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 28 }, ...screeningProjects.map(() => ({ wch: 20 }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Site Comparison');
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `SEIDR_Comparison_${date}.xlsx`);
  }, [screeningProjects, compareSites, aiData]);

  if (loading) {
    return <div className="pp-container"><div className="pp-loading">Loading projects...</div></div>;
  }

  return (
    <div className="pp-container">
      {/* Error banner */}
      {error && (
        <div className="pp-error">{error}</div>
      )}

      {/* Context bar */}
      <div className="pp-context-bar">
        <div className="pp-context-left">
          <div className="pp-tabs">
            <button className={`pp-tab ${tab === 'compare' ? 'pp-tab-active' : ''}`}
                    onClick={() => setTab('compare')}>
              Compare{screeningProjects.length > 0 ? ` (${screeningProjects.length})` : ''}
            </button>
            <button className={`pp-tab ${tab === 'pipeline' ? 'pp-tab-active' : ''}`}
                    onClick={() => setTab('pipeline')}>
              Pipeline{activeProjects.length > 0 ? ` (${activeProjects.length})` : ''}
            </button>
            <button className={`pp-tab ${tab === 'timeline' ? 'pp-tab-active' : ''}`}
                    onClick={() => setTab('timeline')}>
              Timeline
            </button>
          </div>
        </div>
        <div className="pp-context-right">
          {(sparseProjects.length > 0 || enrichingCount > 0) && (
            isPremium ? (
              <button className="pp-enrich-btn" onClick={handleEnrichAll}
                      disabled={enrichingCount > 0}>
                {enrichingCount > 0 ? `Enriching (${enrichingCount})...` : `Enrich ${sparseProjects.length} incomplete`}
              </button>
            ) : (
              <PremiumLock category="Enrich incomplete" variant="lock">
                <button className="pp-enrich-btn" type="button">
                  Enrich {sparseProjects.length} incomplete
                </button>
              </PremiumLock>
            )
          )}
          {isPremium ? (
            <button onClick={() => window.print()} className="pp-action-btn">Export PDF</button>
          ) : (
            <PremiumLock category="Export PDF" variant="lock">
              <button className="pp-action-btn" type="button">Export PDF</button>
            </PremiumLock>
          )}
          {isPremium ? (
            <button onClick={exportToExcel} className="pp-action-btn" disabled={!screeningProjects.length}>Export XLSX</button>
          ) : (
            <PremiumLock category="Export XLSX" variant="lock">
              <button className="pp-action-btn" type="button">Export XLSX</button>
            </PremiumLock>
          )}
          {isPremium ? (
            <button onClick={handleRefreshAll} className="pp-action-btn"
                    disabled={!screeningProjects.length || enrichingCount > 0}
                    title="Re-fetch enrichment data for every saved site (catches new fields added since save)">
              {enrichingCount > 0 ? `Refreshing (${enrichingCount})...` : 'Refresh all'}
            </button>
          ) : (
            <PremiumLock category="Refresh all" variant="lock">
              <button className="pp-action-btn" type="button" title="Re-enrich every saved site — available in Premium">
                Refresh all
              </button>
            </PremiumLock>
          )}
          <AddSiteForm onAdd={saveProject} freeCapHit={freeCapHit} />
        </div>
      </div>

      {/* ── Compare tab ────────────────────────────────────────── */}
      {tab === 'compare' && (
        compareSites.length === 0 ? (
          <div className="pp-empty">
            <div className="pp-empty-title">No sites in screening</div>
            <div className="pp-empty-text">
              Search for sites on the map and click "Save" to add them here for comparison.
            </div>
          </div>
        ) : (
          <div className="pp-compare">
            {/* Sprint E · Task 7 — print-only report. Hidden on screen
                via @media screen rule; window.print() captures only this. */}
            <PrintReport sites={compareSites} computeScore={calcScoreFromReadiness} criteria={CRITERIA} />

            <div className="pp-matrix-container">
              <table className="pp-matrix">
                <thead>
                  {/* Site cards row */}
                  <tr className="pp-site-header-row">
                    <th className="pp-corner-cell" />
                    {compareSites.map(s => {
                      const p = s._project;
                      const { pct, rating } = s.readiness_score != null
                        ? calcScoreFromReadiness(s)
                        : { pct: null, rating: 'Pending' };
                      return (
                        <th key={p.id} className="pp-site-header-cell">
                          <div className="pp-site-compact">
                            <img src={thumbUrl(p.lat, p.lon)} className="pp-site-thumb" alt=""
                                 onError={e => { e.target.style.display = 'none'; }} />
                            <div className="pp-site-compact-info">
                              <a href={`#/projects/${p.id}`} className="pp-site-compact-label">
                                {p.site_label}
                              </a>
                              <div className="pp-site-compact-meta">
                                {(s.total_area_ha || s.areal_ha) ? `${Number(s.total_area_ha || s.areal_ha).toFixed(0)} ha` : ''}
                                {s.nearest_grid_voltage_kv ? ` · ${s.nearest_grid_voltage_kv} kV` : ''}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                                <span className="pp-score-pill" style={{ color: tierForIndex(pct).color, background: `${tierForIndex(pct).color}22` }}>
                                  {pct != null ? `${pct} SEIDR · ${rating}` : 'SEIDR Index pending'}
                                </span>
                                {pct != null && <SeidrIndexExplainer />}
                              </div>
                            </div>
                            <button className="pp-site-remove" onClick={() => deleteProject(p.id)} title="Drop project">×</button>
                            {(isSparse(p) || isEnriching(p.id)) && (
                              <button className="pp-site-enrich"
                                      onClick={() => handleEnrichOne(p.id)}
                                      disabled={isEnriching(p.id)}>
                                {isEnriching(p.id) ? 'Enriching...' : 'Enrich data'}
                              </button>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* ── Verdict row (audit 2026-05-28) ────────────────
                      Compact "answer-before-scrolling" summary: score
                      from backend readiness, plus auto-generated
                      Best-for / Watch-out tags pulled from the same
                      best/worst computation that drives the row
                      highlighting below. Lives in its own group so the
                      group-header styling stays consistent. */}
                  <tr>
                    <td colSpan={compareSites.length + 1} className="pp-group-header">Verdict</td>
                  </tr>
                  <tr className="pp-score-row">
                    <td className="pp-criterion-label pp-score-label">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span>SEIDR Index</span>
                        <SeidrIndexExplainer />
                      </div>
                    </td>
                    {compareSites.map((s, siteIdx) => {
                      // Single source of truth — backend readiness_score.
                      if (s.readiness_score == null) {
                        return (
                          <td key={s._project.id} className="pp-score-cell"
                              style={{ fontSize: 12, color: '#94a3b8' }}>
                            Score pending<br/>
                            <span style={{ fontSize: 10, opacity: 0.8 }}>
                              click Enrich data
                            </span>
                          </td>
                        );
                      }
                      const breakdown = calcScoreFromReadiness(s);
                      const undersized = verdictPerSite[siteIdx]?.undersized;
                      // Force amber when site can't fit the requested load,
                      // even if the index components otherwise scored
                      // it Suitable. Prevents a "75 Suitable" headline
                      // on a site that physically won't hold the project.
                      const color = undersized ? '#f59e0b' : tierForIndex(breakdown.pct).color;
                      const rating = undersized ? 'Undersized · review' : breakdown.rating;
                      return (
                        <td key={s._project.id} className="pp-score-cell">
                          <ScoreCell
                            pct={breakdown.pct}
                            rating={rating}
                            breakdown={breakdown}
                            color={color}
                            suffixClass="pp-score-suffix"
                          />
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="pp-criterion-label">Best for</td>
                    {verdictPerSite.map(({ site, bestRows }) => (
                      <td key={site._project.id} className="pp-criterion-cell"
                          style={{ fontSize: 12, lineHeight: 1.45 }}>
                        {bestRows.length === 0 ? '—' :
                          bestRows.slice(0, 2).map(r => r.label).join(' · ')}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="pp-criterion-label">Watch out</td>
                    {verdictPerSite.map(({ site, worstRows }) => (
                      <td key={site._project.id} className="pp-criterion-cell"
                          style={{ fontSize: 12, lineHeight: 1.45,
                                   color: worstRows.length > 0 ? '#fca5a5' : undefined }}>
                        {worstRows.length === 0 ? '—' :
                          worstRows.slice(0, 2).map(r => r.label).join(' · ')}
                      </td>
                    ))}
                  </tr>

                  {/* ── Differing rows (per-group) ─────────────────────
                      Only rows where values actually differ between sites.
                      Best cell in each row (when dir is tagged) gets a
                      bold + outlined treatment so the reader can spot
                      the winner without reading every cell. */}
                  {groups.map(group => (
                    <React.Fragment key={group}>
                      <tr>
                        <td colSpan={compareSites.length + 1} className="pp-group-header">{group}</td>
                      </tr>
                      {differingRows.filter(r => r.cr.g === group).map(({ cr, cells }) => (
                        <tr key={cr.l}>
                          <td className="pp-criterion-label">
                            {cr.l}
                            {cr.field && <ProvenanceIcon field={cr.field} country={[...compareCountries][0] || 'FI'} />}
                          </td>
                          {cells.map(cell => (
                            <td key={cell.siteId} className="pp-criterion-cell"
                                style={{
                                  background: cellBg[cell.color],
                                  color: cell.color === 'gray' && cell.isBest ? '#e2e8f0' : cellFg[cell.color],
                                  fontWeight: cell.isBest ? 700 : undefined,
                                  boxShadow: cell.isBest ? 'inset 0 0 0 1.5px rgba(34,197,94,0.55)' : undefined,
                                }}>
                              {cell.display}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}

                  {/* ── Shared attributes (collapsed by default) ───────
                      Rows where every site shows the same value carry
                      no decision signal — collapse them so they don't
                      drown out the differentiating ones. When expanded,
                      re-group by category so the reader can find a row
                      (instead of staring at a 15-row flat dump). */}
                  {identicalRows.length > 0 && (
                    <>
                      <tr>
                        <td colSpan={compareSites.length + 1} className="pp-group-header"
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setShowShared(s => !s)}>
                          {showShared ? '▾' : '▸'} Shared attributes ({identicalRows.length} identical across all sites)
                        </td>
                      </tr>
                      {showShared && [...new Set(identicalRows.map(r => r.cr.g))].map(group => (
                        <React.Fragment key={`shared-grp-${group}`}>
                          <tr>
                            <td colSpan={compareSites.length + 1}
                                className="pp-group-header"
                                style={{ opacity: 0.7, fontSize: 10, padding: '6px 12px' }}>
                              ↳ {group}
                            </td>
                          </tr>
                          {identicalRows.filter(r => r.cr.g === group).map(({ cr, cells }) => (
                            <tr key={`shared-${cr.l}`} style={{ opacity: 0.65 }}>
                              <td className="pp-criterion-label">
                                {cr.l}
                                {cr.field && <ProvenanceIcon field={cr.field} country={[...compareCountries][0] || 'FI'} />}
                              </td>
                              {cells.map(cell => (
                                <td key={cell.siteId} className="pp-criterion-cell"
                                    style={{ background: cellBg[cell.color], color: cellFg[cell.color] }}>
                                  {cell.display}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* AI Analysis section removed 2026-05-28 — the seidr.makers.bot
                grid-feasibility upstream is unreliable and the table doesn't
                pay rent next to the Compare grid. Helper functions
                (fetchGridFeasibility, runAiAnalysis) and aiData/aiLoading
                state are now dead code; clean up in a follow-up. */}
          </div>
        )
      )}

      {/* ── Timeline tab (Sprint F · Feature 1) ─────────────────
          Premium-only: free users see the tab but the content is
          replaced by a locked card. */}
      {tab === 'timeline' && (
        isPremium
          ? <TimelineView />
          : <LockedCard
              title="Timeline — Premium"
              body="Track every project's stage gates, deadlines, and owner across a single timeline view."
            />
      )}

      {/* ── Pipeline tab ───────────────────────────────────────── */}
      {tab === 'pipeline' && !isPremium && (
        <LockedCard
          title="Pipeline — Premium"
          body="Manage your active project pipeline (land options, grid applications, permitting, investment) in one table."
        />
      )}
      {tab === 'pipeline' && isPremium && (
        activeProjects.length === 0 ? (
          <div className="pp-empty">
            <div className="pp-empty-title">No projects yet</div>
            <div className="pp-empty-text">Save sites from the map to start tracking them.</div>
          </div>
        ) : (
          <div className="pp-pipeline">
            <table className="pp-pipeline-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Area</th>
                  <th>Grid</th>
                  <th>Score</th>
                  <th>Stage</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {activeProjects.map(p => {
                  const e = p.enrichment || {};
                  // Single source of truth: backend readiness_score.
                  // Pending state when not yet enriched.
                  const { pct } = e.readiness_score != null
                    ? calcScoreFromReadiness(e)
                    : { pct: null };
                  return (
                    <tr key={p.id} className="pp-pipeline-row"
                        onClick={() => { window.location.hash = `#/projects/${p.id}`; }}>
                      <td className="pp-pl-name">
                        <img src={thumbUrl(p.lat, p.lon)} className="pp-pl-thumb" alt=""
                             onError={ev => { ev.target.style.display = 'none'; }} />
                        <span>{p.site_label}</span>
                      </td>
                      <td>{(e.total_area_ha || e.areal_ha) ? `${Number(e.total_area_ha || e.areal_ha).toFixed(0)} ha` : '—'}</td>
                      <td>{e.nearest_grid_voltage_kv ? `${e.nearest_grid_voltage_kv} kV · ${(e.nearest_grid_distance_km || 0).toFixed(1)} km` : '—'}</td>
                      <td style={{ color: pct != null ? scoreColor(pct) : '#94a3b8', fontWeight: pct != null ? 700 : 400, fontSize: pct != null ? undefined : 11 }}>
                        {pct ?? 'pending'}
                      </td>
                      <td onClick={ev => ev.stopPropagation()}>
                        <select className="pp-pl-stage" value={p.stage}
                                onChange={ev => handleStageChange(p, ev.target.value)}>
                          {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                          <option value="dropped">Drop</option>
                        </select>
                      </td>
                      <td className="pp-pl-updated">{daysSince(p.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Dropped projects */}
            {projects.filter(p => p.stage === 'dropped').length > 0 && (
              <details className="pp-dropped">
                <summary>Dropped ({projects.filter(p => p.stage === 'dropped').length})</summary>
                {projects.filter(p => p.stage === 'dropped').map(p => (
                  <div key={p.id} className="pp-dropped-item">
                    <span>{p.site_label}</span>
                    <button onClick={() => updateProject(p.id, { stage: 'candidate' })}>Restore</button>
                  </div>
                ))}
              </details>
            )}
          </div>
        )
      )}

      <style>{`
        @media print {
          .header-top, .pp-context-bar button, .pp-site-remove, select { display: none !important; }
          body { background: white !important; color: black !important; }
          td, th { color: black !important; }
        }
      `}</style>
    </div>
  );
}
